import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentMode } from '@/contexts/PaymentModeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, CreditCard, Key, Clock, ShoppingCart, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Account() {
  const { user, logout, refreshUser, token } = useAuth();
  const paymentMode = usePaymentMode();
  const navigate = useNavigate();
  const [licenseKey, setLicenseKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [payhipLink, setPayhipLink] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Change password states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchPayhipLink = async () => {
      const response = await axios.get(`${API}/payhip/product-link`);
      setPayhipLink(response.data.link);
    };
    fetchPayhipLink();
  }, []);

  useEffect(() => {
    if (user?.time_remaining_seconds) {
      setTimeRemaining(user.time_remaining_seconds);
      
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 0) {
            clearInterval(interval);
            refreshUser();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return 'Expiré';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (days > 0) {
      return `${days}j ${hours}h ${minutes}min`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}min ${secs}s`;
    } else {
      return `${minutes}min ${secs}s`;
    }
  };

  const handleActivateLicense = async (e) => {
    e.preventDefault();
    setActivating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(
        `${API}/license/activate`,
        { license_key: licenseKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(response.data.message);
      setLicenseKey('');
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'License key invalide');
    } finally {
      setActivating(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    
    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('Veuillez remplir tous les champs');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    try {
      setChangingPassword(true);
      
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordSuccess('Mot de passe changé avec succès !');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess(null);
      }, 2000);
      
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="bg-black/90 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <h1 className="font-bebas text-3xl text-netflix-red">WEBEOS</h1>
          <Button onClick={() => navigate('/')} variant="outline" className="border-white/20">
            Retour
          </Button>
        </div>
      </div>

      <div className="px-4 md:px-8 py-12 max-w-4xl mx-auto space-y-6">
        <Card className="bg-bg-secondary border-white/10">
          <CardHeader>
            <CardTitle className="font-work text-white">Mon Compte</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-text-secondary text-sm font-work">Email</p>
              <p className="text-white font-inter">{user.email}</p>
            </div>
            
            <div>
              <p className="text-text-secondary text-sm font-work mb-2">Statut d&apos;accès</p>
              {/* Access status - Show different message in free mode */}
              {paymentMode.enabled ? (
                // Paid mode: Show access status normally
                user.has_access ? (
                  <div className="p-4 bg-green-900/20 border border-green-500/50 rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-green-400" />
                      <p className="text-green-200 font-work font-semibold">Accès Actif</p>
                    </div>
                    <p className="text-2xl font-bebas text-white">
                      Expire dans : {formatTime(timeRemaining)}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-md">
                    <p className="text-red-200 font-work font-semibold">Aucun accès actif</p>
                    <p className="text-text-secondary text-sm mt-1">Achetez un code pour accéder aux vidéos</p>
                  </div>
                )
              ) : (
                // Free mode: Show free access message
                <div className="p-4 bg-green-900/20 border border-green-500/50 rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🎁</span>
                    <p className="text-green-200 font-work font-semibold">Accès Gratuit Illimité</p>
                  </div>
                  <p className="text-text-secondary text-sm">
                    Profitez de tout le catalogue sans limite de temps !
                  </p>
                </div>
              )}
            </div>
            
            <Button onClick={logout} variant="outline" className="border-white/20 text-white w-full">
              Déconnexion
            </Button>
            
            <Button 
              onClick={() => setShowPasswordChange(true)} 
              className="bg-blue-600 hover:bg-blue-700 text-white w-full"
            >
              <Key className="w-4 h-4 mr-2" />
              Changer le mot de passe
            </Button>
          </CardContent>
        </Card>

        {/* Change Password Modal */}
        {showPasswordChange && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-bg-secondary border-white/10 w-full max-w-md">
              <CardHeader>
                <CardTitle className="font-work text-white flex items-center gap-2">
                  <Key className="w-6 h-6 text-blue-500" />
                  Changer le mot de passe
                </CardTitle>
                <CardDescription className="text-text-secondary">
                  Entrez votre ancien mot de passe et choisissez-en un nouveau
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-text-secondary font-work mb-2 block">
                    Ancien mot de passe
                  </label>
                  <Input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="bg-bg-tertiary border-white/20 text-white"
                    placeholder="Ancien mot de passe"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-text-secondary font-work mb-2 block">
                    Nouveau mot de passe
                  </label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-bg-tertiary border-white/20 text-white"
                    placeholder="Nouveau mot de passe"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-text-secondary font-work mb-2 block">
                    Confirmer le nouveau mot de passe
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-bg-tertiary border-white/20 text-white"
                    placeholder="Confirmer le mot de passe"
                  />
                </div>
                
                {passwordError && (
                  <div className="p-3 bg-red-900/20 border border-red-500/50 rounded-md">
                    <p className="text-red-200 text-sm">{passwordError}</p>
                  </div>
                )}
                
                {passwordSuccess && (
                  <div className="p-3 bg-green-900/20 border border-green-500/50 rounded-md">
                    <p className="text-green-200 text-sm">{passwordSuccess}</p>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changement...
                      </>
                    ) : (
                      'Confirmer'
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => {
                      setShowPasswordChange(false);
                      setOldPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                    variant="outline"
                    className="border-white/20"
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Premium/Payment Section - Only show if payment mode is enabled */}
        {paymentMode.enabled && (!user.has_access || user.is_trial) && (
          <Card className="bg-bg-secondary border-white/10">
            <CardHeader>
              <CardTitle className="font-work text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-netflix-red" />
                {user.is_trial ? "Passer à l'Abonnement Premium" : "Obtenir l'Accès"}
              </CardTitle>
              <CardDescription className="text-text-secondary">
                {user.is_trial 
                  ? "Prolongez votre accès avec 30 jours complets - 8,40€"
                  : "Accès illimité pendant 30 jours - 8,40€"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-netflix-red text-white w-8 h-8 rounded-full flex items-center justify-center font-bebas text-xl flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-work font-semibold text-white mb-2">Acheter sur Payhip</h3>
                    <p className="text-text-secondary text-sm mb-4">Paiement sécurisé via PayPal</p>
                    <a
                      href={`${payhipLink}?email=${encodeURIComponent(user.email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full bg-netflix-red hover:bg-netflix-red-hover py-6 text-lg">
                        <CreditCard className="w-5 h-5 mr-2" />
                        Acheter pour 8,40€
                      </Button>
                    </a>
                    <p className="text-text-secondary text-xs mt-2 font-inter">
                      La license key sera envoyée à : <span className="text-white font-semibold">{user.email}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="bg-netflix-red text-white w-8 h-8 rounded-full flex items-center justify-center font-bebas text-xl flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-work font-semibold text-white mb-2">Activer votre License Key</h3>
                    <p className="text-text-secondary text-sm mb-4">
                      Après paiement, vous recevrez une license key par email. Collez-la ici :
                    </p>
                    <form onSubmit={handleActivateLicense} className="space-y-3">
                      <Input
                        type="text"
                        placeholder="Ex: WTKP4-66NL5-HMKQW-GFSCZ"
                        value={licenseKey}
                        onChange={(e) => setLicenseKey(e.target.value)}
                        className="bg-bg-tertiary border-white/20 text-white font-mono"
                        required
                      />
                      <Button
                        type="submit"
                        disabled={activating}
                        className="w-full bg-netflix-red hover:bg-netflix-red-hover py-6 text-lg"
                      >
                        {activating ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Vérification...</>
                        ) : (
                          <><Key className="w-5 h-5 mr-2" /> Activer l&apos;accès 30 jours</>
                        )}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-md">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-900/20 border border-green-500/50 rounded-md flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-green-200 font-work font-semibold">{success}</p>
                    <p className="text-green-300 text-sm mt-1">Vous pouvez maintenant regarder tous les films !</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}